import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Router} from "@angular/router";
import {ViewModelType} from "../../../core/models/type/view-model-type.enum";
import {UtilService} from "../../../core/services/util/util.service";
import {TableOperationButton} from "../../../core/services/table-operation/table-operation-button";
import {TableOperationService} from "../../../core/services/table-operation/table-operation.service";
import {ContextMenu} from "../../../core/services/context-menu/context-menu";
import {MenuEventArgs, MenuItemModel} from "@syncfusion/ej2-angular-navigations";
import {Subscription} from "rxjs";
import {FormContext} from "../../../core/models/type/form-context.enum";

@Component({
  selector: 'sb-content-page-footer',
  templateUrl: './content-page-footer.component.html',
  styleUrls: ['./content-page-footer.component.scss']
})
export class ContentPageFooterComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  goBack?: () => void;

  @Input()
  doClose?: () => void;

  @Input()
  contentModel: any; // ListContentModel or DetailContentModel

  private contentModelSubscription: Subscription;

  tableOperationButtons: TableOperationButton[];
  leftTableOperationButtons: TableOperationButton[];
  rightTableOperationButtons: TableOperationButton[];
  workflowButtons: TableOperationButton[];

  outerWrapperClass = "v2";
  innerWrapperClass = "page-footer";

  MessageBus = (window as any).KO_MESSAGE_BUS_BRIDGE;

  // A subscription for forcing the footer to reload. Occurs in some circumstances where a related
  // record (e.g. an Approval Task) updates a related record
  refreshFooterSubscription = this.MessageBus.subscribe(
    this.MessageBus.CHANNELS.FORM_API_EVENT,
    `footer.reload`,
    async (data: any, envelope: any) => {
      let currentID = this.contentModel?.data?.id?.();

      if (currentID != null && currentID === data?.id) {
        this.rebuildFooter();
      }
    }
  );

  constructor(
    private router: Router,
    private tableOperationService: TableOperationService,
    private utilService: UtilService,
    private ref: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contentModel'] && changes['contentModel'].currentValue) {

      this.contentModelSubscription = this.contentModel.changed$.subscribe((event) => {
        if (typeof event == 'string') {
          this.rebuildFooter();
        }
      });

      this.rebuildFooter();
    }
  }

  ngOnDestroy() {
    this.contentModelSubscription && this.contentModelSubscription.unsubscribe();
    this.MessageBus.unsubscribe(this.refreshFooterSubscription);
  }

  back() {
    // custom back for this specific page:
    if (this.goBack) return this.goBack();
    // table operation back button:
    const backButton = this.tableOperationButtons?.find(item => item.cssClass === 'back-button');
    if (backButton) return backButton.actionCallback(null);
    // standard back button:
    return this.router.navigate(['/']);
  }

  rebuildFooter() {
    // Dealing with the various places the footer can appear
    if (this.contentModel.viewModel.formContext !== FormContext.DETAIL_MAIN) {
      this.goBack = null;
    }

    this.tableOperationButtons = this.showOperations() ? this.getTableOperationButtons() : [];
    this.leftTableOperationButtons = this.tableOperationButtons.filter(b => !b.cssClass.includes('right') && !b.cssClass.includes('back-button'));
    this.rightTableOperationButtons = this.tableOperationButtons.filter(b => b.cssClass.includes('right') && !b.cssClass.includes('back-button'));
    this.workflowButtons = this.showWorkflow() ? this.getWorkflowButtons() : [];

    // Angular was not always picking up the changes as they could be triggered by external events, so needed to force
    this.ref.markForCheck();
  }

  showOperations() {
    const viewModel = this.contentModel.viewModel;
    if (viewModel.getViewModelType() === ViewModelType.LIST) {
      return true;
    } else {
      const data = this.contentModel.data;
      const hasOperations = data._tableOperations && UtilService.unwrapObservable(data._tableOperations).length > 0;
      const showOperations = viewModel.viewDefinition().properties.showTableOperations;
      return (hasOperations && showOperations);
    }
  }

  showWorkflow() {
    const viewModel = this.contentModel.viewModel;
    if (viewModel.getViewModelType() === ViewModelType.LIST) {
      return false;
    } else {
      const viewDefinition = viewModel.viewDefinition();
      const data = this.contentModel.data;
      const hasWorkflowInformation = !UtilService.isEmptyObject(data._workflow);
      const showTransitions = viewDefinition.properties.showWorkflowTransitions;
      return (hasWorkflowInformation && showTransitions);
    }
  }

  getTableOperationButtons(): TableOperationButton[] {
    return this.tableOperationService.tableOperationButtons(this.contentModel).map((b) => {
      b.contextMenu = this.mapButtonContextMenu(b.contextMenu);
      b.cssClass = this.mapButtonCssClass(b.cssClass);
      return b;
    });
  }

  getWorkflowButtons(): TableOperationButton[] {
    return this.tableOperationService.workflowButtons(this.contentModel).map((b) => {
      b.cssClass = this.mapButtonCssClass(b.cssClass);
      return b;
    });
  }

  mapButtonCssClass(cssClass?: string): string {
    return cssClass ? cssClass.replace('info', 'e-outline').replace('secondary', 'e-outline').replace('alert', 'e-danger').replace('warning', 'e-warning').replace('success', 'e-primary').replace('save-button', 'e-success') : 'e-outline';
  }

  async mapButtonContextMenu(contextMenu: ContextMenu): Promise<MenuItemModel[]> {
    const contextMenuItems = await contextMenu.getMenuItems();

    const menuItems: Promise<MenuItemModel>[] = contextMenuItems.map(async (c) => {
      if (c.isSeparator) {
        return {
          id: this.utilService.generateCSSID(),
          separator: true
        }
      }
      const menuItem: MenuItemModel = {
        id: this.utilService.generateCSSID(),
        iconCss: c.prefixIcon ? `mi mi_${c.prefixIcon}` : '',
        text: c.label,
      };

      (menuItem as any)._actionCallback = c.actionCallback;

      if (c.childMenu) {
        menuItem.items = await this.mapButtonContextMenu(c.childMenu);
      }

      return menuItem;
    });

    return Promise.all(menuItems);
  }

  editContextMenuSelect(e: MenuEventArgs) {
    (e.item as any)._actionCallback(e.event);
  }

  callButtonActionCallback(button: TableOperationButton, $event: any) {
    button.actionCallback($event);
    if (this.doClose) {
      this.doClose();
    }
  }
}
